import { create } from 'zustand';
import { Project, Asset, AppNode, Connection } from '../types';
import { projectApi } from '../services/projectApi';
import { unifiedStorage } from '../services/unifiedStorage';

const CACHE_KEY_PREFIX = 'bongai_project_cache_';
const LAST_SAVE_KEY = 'bongai_last_save_';

function cacheProjectData(projectId: string, data: { nodes: AppNode[]; connections: Connection[]; groups: any[] }) {
    // 时间戳为极小载荷（受淘汰保护），直接写
    try {
        localStorage.setItem(LAST_SAVE_KEY + projectId, new Date().toISOString());
    } catch { /* 配额耗尽时由统一安全写的告警兜底，此处静默 */ }
    // 项目数据统一走 UnifiedStorage 预算门 + 安全写（同 key 自愈/跨 key 淘汰）：
    // 超预算载荷跳过 localStorage 层（IndexedDB 为主存储），
    // 消除裸写导致的 QuotaExceededError 与配额挤占（内部为同步写）
    void unifiedStorage.saveProjectCache(projectId, data);
}

function getCachedProjectData(projectId: string): { nodes: AppNode[]; connections: Connection[]; groups: any[] } | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY_PREFIX + projectId);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        console.warn('读取缓存项目数据失败:', e);
        return null;
    }
}

async function retryFetch<T>(fn: () => Promise<T>, maxRetries: number = 3, delayMs: number = 1000): Promise<T> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn();
        } catch (e: any) {
            if (attempt < maxRetries - 1 && (e.message?.includes('fetch') || e.message?.includes('network') || e.message?.includes('Failed'))) {
                console.warn(`请求失败，${delayMs * (attempt + 1)}ms 后重试 (${attempt + 1}/${maxRetries})...`);
                await new Promise(r => setTimeout(r, delayMs * (attempt + 1)));
                continue;
            }
            throw e;
        }
    }
    throw new Error('重试次数已用完');
}

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  currentProjectId: string | null;
  assets: Asset[];
  isLoading: boolean;
  error: string | null;
  /** 项目列表加载失败信息（独立于 error：仅代表「列表没加载出来」，UI 据此区分空列表与加载失败） */
  loadError: string | null;
  
  loadProjects: () => Promise<void>;
  createProject: (name: string) => Promise<Project>;
  loadProject: (projectId: string) => Promise<void>;
  saveCurrentProject: () => Promise<void>;
  deleteProject: (projectId: string) => Promise<boolean>;
  renameProject: (projectId: string, newName: string) => Promise<boolean>;
  setCurrentProject: (project: Project | null) => void;
  updateWorkflowData: (nodes: AppNode[], connections: Connection[], groups: any[]) => void;
  loadAssets: (projectId: string) => Promise<void>;
  addAsset: (asset: Asset) => void;
  clearProjects: () => void;
  clearError: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  currentProjectId: null,
  assets: [],
  isLoading: false,
  error: null,
  loadError: null,

  loadProjects: async () => {
    set({ isLoading: true, error: null, loadError: null });
    try {
      const result = await retryFetch(() => projectApi.listProjects());
      set({ projects: result.projects, isLoading: false });
    } catch (e: any) {
      set({ error: e.message || '加载项目列表失败', loadError: e.message || '加载项目列表失败', isLoading: false });
    }
  },
  
  createProject: async (name: string) => {
    set({ isLoading: true, error: null });
    try {
      const project = await retryFetch(() => projectApi.createProject(name));
      set(state => ({
        projects: [project, ...state.projects],
        currentProject: project,
        currentProjectId: project.id,
        assets: [],
        isLoading: false,
      }));
      return project;
    } catch (e: any) {
      set({ error: e.message || '创建项目失败', isLoading: false });
      throw e;
    }
  },
  
  loadProject: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const project = await retryFetch(() => projectApi.getProject(projectId));
      set({
        currentProject: project,
        currentProjectId: projectId,
        assets: project.assets || [],
        isLoading: false,
      });
      if (project.workflow_data) {
        cacheProjectData(projectId, {
          nodes: project.workflow_data.nodes || [],
          connections: project.workflow_data.connections || [],
          groups: project.workflow_data.groups || [],
        });
      }
    } catch (e: any) {
      const cached = getCachedProjectData(projectId);
      if (cached) {
        console.warn('从后端加载项目失败，使用本地缓存:', e.message);
        set({
          currentProject: {
            id: projectId,
            name: '离线缓存项目',
            workflow_data: cached,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          currentProjectId: projectId,
          assets: [],
          isLoading: false,
          error: '后端连接失败，已从本地缓存恢复',
        });
      } else {
        set({ error: e.message || '加载项目失败', isLoading: false });
      }
    }
  },
  
  saveCurrentProject: async () => {
    const { currentProject, currentProjectId } = get();
    if (!currentProject || !currentProjectId) return;
    
    // Always use the ground-truth node state from nodeStore so edits are not lost.
    const nodeStoreState = (await import('./nodeStore')).useNodeStore.getState();
    const workflow_data = {
      nodes: nodeStoreState.nodes || [],
      connections: nodeStoreState.connections || [],
      groups: (currentProject.workflow_data && 'groups' in currentProject.workflow_data)
        ? currentProject.workflow_data.groups
        : [],
    };
    cacheProjectData(currentProjectId, workflow_data);
    
    try {
      await retryFetch(() => projectApi.updateProject(currentProjectId, { workflow_data }));
    } catch (e: any) {
      set({ error: e.message || '保存项目失败（已缓存到本地）' });
    }
  },
  
  deleteProject: async (projectId: string) => {
    try {
      await retryFetch(() => projectApi.deleteProject(projectId));
      localStorage.removeItem(CACHE_KEY_PREFIX + projectId);
      localStorage.removeItem(LAST_SAVE_KEY + projectId);
      set(state => ({
        projects: state.projects.filter(p => p.id !== projectId),
        currentProject: state.currentProjectId === projectId ? null : state.currentProject,
        currentProjectId: state.currentProjectId === projectId ? null : state.currentProjectId,
        assets: state.currentProjectId === projectId ? [] : state.assets,
      }));
      return true;
    } catch (e: any) {
      set({ error: e.message || '删除项目失败' });
      return false;
    }
  },

  renameProject: async (projectId: string, newName: string) => {
    try {
      await retryFetch(() => projectApi.updateProject(projectId, { name: newName }));
      set(state => ({
        projects: state.projects.map(p =>
          p.id === projectId ? { ...p, name: newName } : p
        ),
        currentProject: state.currentProjectId === projectId
          ? { ...state.currentProject!, name: newName }
          : state.currentProject,
      }));
      return true;
    } catch (e: any) {
      set({ error: e.message || '重命名项目失败' });
      return false;
    }
  },
  
  setCurrentProject: (project: Project | null) => {
    set({
      currentProject: project,
      currentProjectId: project?.id || null,
      assets: project?.assets || [],
    });
  },
  
  updateWorkflowData: (nodes: AppNode[], connections: Connection[], groups: any[]) => {
    const { currentProject, currentProjectId } = get();
    if (!currentProject) return;
    
    const workflow_data = { nodes, connections, groups };
    set({
      currentProject: {
        ...currentProject,
        workflow_data,
      },
    });
    
    if (currentProjectId) {
      cacheProjectData(currentProjectId, workflow_data);
    }
  },
  
  loadAssets: async (projectId: string) => {
    try {
      const assets = await retryFetch(() => projectApi.listAssets(projectId));
      set({ assets });
    } catch (e: any) {
      set({ error: e.message || '加载资产列表失败' });
    }
  },
  
  addAsset: (asset: Asset) => {
    set(state => ({ assets: [asset, ...state.assets] }));
  },
  
  // 退出登录时清空项目数据，避免残留上一账号内容
  clearProjects: () => set({ projects: [], currentProject: null, currentProjectId: null, assets: [], error: null, loadError: null }),

  clearError: () => set({ error: null }),
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
